import { EffettivoPresunto } from 'constants/inspections';

// Mapping "in" per la tab L3 (Idraulica).
// Contratto campi diagnosis.L3.* fissato in
// apps/web-bo/plans/features/l3-idraulica-tab.md — il FE scrive/legge
// esattamente questi nomi, corrispondenti 1:1 a `Level3` in
// apps/api/src/inspections/models/diagnosis.ts.
//
// Decisione di prodotto: i campi condivisi con L0/L1 sono una "copia
// indipendente". Storicamente il prefill one-shot sotto era responsabilità
// del FE (prima volta che si apre la tab L3, diagnosis.L3 vuoto).
//
// Aggiornamento 2026-08-10: il BE ora fa lo stesso prefill server-side, alla
// CREAZIONE dell'attività L3 (`importL012FieldsIntoL3`,
// apps/api/src/inspections/extensions/l3-shared-fields.ts — stesso elenco di
// 13 campi "R" di sotto, fonte di verità ora condivisa BE+FE) — quindi
// `diagnosis.L3` arriva quasi sempre già popolato al primo GET, e il ramo
// `l3AlreadySaved` sotto scatta comunque (spread normalizzato, vedi
// normalizeL3Field più sotto). Il prefill locale sotto resta come fallback
// difensivo per i casi limite non coperti dalla propagazione BE (es. la
// primissima attività in assoluto su un ponte, se fosse L3 senza nulla da
// cui ereditare) — non è più il percorso principale, ma è innocuo lasciarlo.

// 159b — stessa logica di Accordion4HydraulicRiskL1.jsx/mappingInL1.js: le
// checkbox Effettiva/Presunta non sono salvate a parte (mappingOutL3.js non
// le invia), si ricostruiscono dal flag già persistito.
// Issue #1021 punto 43 (handoff BE issue-1021-riapertura-2026-09-29-handoff-fe.md)
// — `Number(flag)`: il BE ha confermato che il flag può tornare persistito
// come stringa numerica ("1" invece di 1); il confronto `===` con l'enum
// (numerico) falliva silenziosamente in quel caso, lasciando entrambe le
// checkbox scoperte pur con un valore salvato.
// Issue #1021 punto 13 (riapertura 2026-09-30) — `false`, non `undefined`,
// quando il flag è vuoto: le due RHFCheckbox in Accordion4ErosionL3.jsx
// hanno `defaultValue={false}`, quindi react-hook-form le materializza a
// `false` in _formValues appena montano, mentre il baseline di reset()
// restava a `undefined` — `isDirty` (confronto profondo _defaultValues vs
// _formValues) li vedeva sempre diversi, per sempre, su qualunque attività
// con 159b non ancora valorizzato — senza che l'utente toccasse mai quelle
// due checkbox (dirtyFields infatti restava vuoto: nessun input le aveva
// mai segnate). Nessuna differenza visiva: RHFCheckbox fa già
// `checked={!!field.value}`, `undefined` e `false` rendono identici.
const deriveProfonditaFondazioneRispettoAlveoCheckboxes = flag => ({
  profonditaFondazioneRispettoAlveoEffettiva: flag
    ? Number(flag) === EffettivoPresunto.Effettivo
    : false,
  profonditaFondazioneRispettoAlveoPresunta: flag
    ? Number(flag) === EffettivoPresunto.Presunto
    : false,
});

// Bug "banner modifiche non salvate resta acceso subito dopo il
// salvataggio" (segnalato dall'utente 2026-09-02) — stessa famiglia del
// punto 13 (mismatch tra rappresentazione "vuota" del BE e quella attesa
// dal form), meccanismo diverso: mappingDataOutL3.js converte OGNI campo
// vuoto in `null` al salvataggio (`|| null` su ogni riga), ma il ramo
// `l3AlreadySaved` sotto faceva fin qui uno spread grezzo di `diagnosis.L3`
// senza normalizzare quei `null` — a differenza del ramo di fallback più in
// basso, che lo fa esplicitamente campo per campo. reset() stabiliva così
// un baseline con `null` su ogni campo lasciato vuoto, mentre i wrapper RHF
// (RHFSelect/RHFNumberTextField/RHFTextField) si aspettano `''` (o `[]` per
// i multiselect) come "vuoto" — un componente che rimonta o si riallinea al
// proprio `defaultValue` dopo il reset (es. i campi condizionali
// estensioneProtezioneAlPiede/statoConservazioneProtezionePiede in
// Accordion4ErosionL3.jsx, montati solo se protezioneAlPiedePileSpalle=Sì)
// diverge dal baseline `null`, rimettendo isDirty a true senza alcun input
// utente. dataAttivita è escluso di proposito: ha già un proprio
// meccanismo di auto-correzione (Accordion1GeneralInfoL3.jsx, useEffect con
// reset() dedicato), normalizzarlo qui rischierebbe di interferire.
const L3_ARRAY_FIELDS = new Set([
  'analisiFenomenoAnalizzato',
  'esitoDellaValutazione',
  'ispezioniSpecialiFenomeni',
  'indicatoriIspezioniSpecialiSormonto',
  'indicatoriIspezioniSpecialiErosione',
  'inquadramentoTerritorialeImmagini',
  'documentazioneDisponibileImmagini',
  'descrizioneStrutturaImmagini',
  'caratterizzazioneSistemaImmagini',
  'scenariIdrauliciRiferimentoImmagini',
  'valutazioneRischioSormontoImmagini',
  'valutazioneFenomeniErosiviImmagini',
]);

// 609 diventa array solo per le attività risalvate dopo la conversione BE
// (2026-09-17): un'attività L3 salvata prima, non ancora risalvata, ha
// ancora nel blob raw uno scalare (o, se molto vecchia, una label legacy) —
// RHFMultiSelect crasha (`selected.map` in renderValue) se riceve un valore
// non-array, quindi lo scalare va sempre wrappato in un array qui. Il caso
// label legacy resta non gestito di proposito (basta risalvare l'attività,
// vedi handoff-fe-task-20-box-overview-l3.md §7).
const normalizeL3Field = (fieldName, value) => {
  if (fieldName === 'dataAttivita' || value !== null) {
    if (
      fieldName === 'esitoDellaValutazione' &&
      value !== null &&
      !Array.isArray(value)
    ) {
      return [value];
    }
    return value;
  }
  return L3_ARRAY_FIELDS.has(fieldName) ? [] : '';
};

// Issue #1021, riapertura 2026-09-30 — elenco completo 1:1 con `Level3`
// (apps/api/src/inspections/models/diagnosis.ts, la fonte di verità
// dichiarata in testa a questo file), esclusa cdaGraphicalRepresentation
// (oggetto composito, mai wrappato in un componente RHF con un proprio
// `defaultValue` — nessun rischio di divergenza, passata così com'è sotto).
// Necessario iterare su QUESTO elenco invece che su Object.entries(diagnosis.L3):
// su un'attività L3 appena creata il BE non inizializza tutti i ~50 campi,
// molti sono ASSENTI dal blob (non `null`) — Object.entries li salta del
// tutto, lasciandoli fuori dal baseline di reset(), mentre ogni componente
// RHF li materializza comunque al proprio defaultValue ('' o []) al mount:
// stesso identico bug già risolto per le checkbox 159b qui sotto (issue
// #1021 punto 13), esteso qui a tutti i campi del form L3.
const L3_FIELD_NAMES = [
  'analisiFenomenoAnalizzato',
  'rilevatore',
  'dataAttivita',
  'ispezioniSpecialiFenomeni',
  'indicatoriIspezioniSpecialiSormonto',
  'indicatoriIspezioniSpecialiErosione',
  'portataQP3',
  'portataQP2',
  'portataQP1',
  'velocitaAMonteVP3',
  'velocitaAMonteVP2',
  'velocitaAMonteVP1',
  'quotaPeloLiberoP3',
  'quotaPeloLiberoP2',
  'quotaPeloLiberoP1',
  'quotaMinimaFondoAlveo',
  'pendenzaMedia',
  'quotaMinimaIntradosso',
  'spessoreImpalcato',
  'francoIdraulicoFP2',
  'evidenzeProfonditaDelleFondazioni',
  'massimaProfonditaScavoLocalizzata',
  'massimaProfonditaScavoContrazione',
  'massimaProfonditaScavoAltriFenomeni',
  'massimaProfonditaScavoTotale',
  'profonditaFondazioneRispettoAlveo',
  'profonditaFondazioneRispettoAlveoFlag',
  'rapportoDsTotDf',
  'protezioneAlPiedePileSpalle',
  'estensioneProtezioneAlPiede',
  'statoConservazioneProtezionePiede',
  'cdaSormonto',
  'cdaErosione',
  'cdaIdraulica',
  'esitoDellaValutazione',
  'condizioniTransitabilita',
  'inquadramentoTerritorialeDescrizione',
  'inquadramentoTerritorialeImmagini',
  'documentazioneDisponibileDescrizione',
  'documentazioneDisponibileImmagini',
  'descrizioneStrutturaDescrizione',
  'descrizioneStrutturaImmagini',
  'caratterizzazioneSistemaDescrizione',
  'caratterizzazioneSistemaImmagini',
  'scenariIdrauliciRiferimentoDescrizione',
  'scenariIdrauliciRiferimentoImmagini',
  'valutazioneRischioSormontoFrancoIdraulico',
  'valutazioneRischioSormontoImmagini',
  'valutazioneFenomeniErosiviTesto',
  'valutazioneFenomeniErosiviImmagini',
  'conclusioniDescrizione',
];

// Scheletro con un default esplicito ('' / [], via normalizeL3Field) per
// ogni campo del form L3 — usato per completare il ramo di fallback sotto,
// che copre solo i 13 campi condivisi "R" e altrimenti lascerebbe assenti
// tutti gli altri, stesso identico bug del ramo `l3AlreadySaved`.
const emptyL3Skeleton = () =>
  Object.fromEntries(
    L3_FIELD_NAMES.map(fieldName => [
      fieldName,
      normalizeL3Field(fieldName, null),
    ])
  );

export function mappingDataInL3(diagnosis) {
  const l3AlreadySaved =
    !!diagnosis?.L3 && Object.keys(diagnosis.L3).length > 0;

  const base = l3AlreadySaved
    ? {
        ...Object.fromEntries(
          L3_FIELD_NAMES.map(fieldName => [
            fieldName,
            normalizeL3Field(fieldName, diagnosis.L3[fieldName] ?? null),
          ])
        ),
        cdaGraphicalRepresentation: diagnosis.L3.cdaGraphicalRepresentation,
        ...deriveProfonditaFondazioneRispettoAlveoCheckboxes(
          diagnosis.L3.profonditaFondazioneRispettoAlveoFlag
        ),
      }
    : {
        ...emptyL3Skeleton(),
        // Solo le chiavi con un valore reale sovrascrivono lo scheletro:
        // i campi che il fallback risolve a `undefined` (nessun dato da
        // cui ereditare) devono restare sul default pulito, non tornare
        // `undefined` sovrascrivendolo.
        ...Object.fromEntries(
          Object.entries(mappingDataInL3Fallback(diagnosis)).filter(
            ([, value]) => value !== undefined
          )
        ),
      };

  return {
    ...base,
    // Issue #1021, riapertura 2026-09-23 — precompilata con la data odierna
    // se l'attività non ne ha ancora una salvata (dataAttivita non è tra i
    // campi condivisi prefillati dal BE, quindi manca sempre alla primissima
    // apertura di una nuova attività L3, anche quando `l3AlreadySaved` è già
    // vero grazie al prefill server-side degli altri 13 campi). Prima questo
    // prefill viveva in un useEffect separato in
    // Accordion1GeneralInfoL3.jsx (setValue + reset(getValues()) al mount),
    // soggetto a una race reale: gli accordion montano SUBITO (L3Idraulica.jsx
    // non ha alcun gate sul caricamento dati), col form ancora vuoto — quel
    // reset() catturava quindi un baseline "pulito" ma PRIMA che la GET vera
    // (questa funzione, chiamata da handleDetail in useInspectionL3.js)
    // completasse. La reset() successiva, con i dati reali, non includendo
    // dataAttivita (assente dal blob), la riportava a `undefined` — ma
    // l'effetto in Accordion1 ha deps `[]` (solo mount) e non poteva
    // rilanciarsi per rimediare, lasciando un disallineamento tra il valore
    // corrente e il nuovo baseline che faceva risultare isDirty=true (banner
    // "modifiche non salvate") ad attività appena aperta, senza alcun input
    // utente. Ora il prefill fa parte dello stesso reset che stabilisce il
    // baseline reale (qui, non più in un effect separato): nessuna race
    // possibile.
    dataAttivita: base.dataAttivita ?? new Date(),
  };
}

function mappingDataInL3Fallback(diagnosis) {
  // Fallback difensivo (vedi nota sopra) — prefill dei soli campi "R"
  // (riusati, copia indipendente). Nome proprietà sorgente verificato
  // puntualmente in apps/api/src/inspections/models/diagnosis.ts (classi
  // Common/Level0/Level1) — non fidarsi ciecamente in caso di refactor BE
  // futuri, confrontare con l3-shared-fields.ts se disallineato.
  return {
    // 97b — oggi su Level0
    ispezioniSpecialiFenomeni:
      diagnosis?.L0?.fenomeniIspezioniSpecialiRischioIdraulico ?? undefined,
    // 97c — oggi su Level1
    indicatoriIspezioniSpecialiSormonto:
      diagnosis?.L1?.indicatoriIspezioniSpecialiSormonto ?? undefined,
    // 97f — oggi su Level1
    indicatoriIspezioniSpecialiErosione:
      diagnosis?.L1?.indicatoriIspezioniSpecialiErosione ?? undefined,
    // 139 — oggi su Level1
    quotaPeloLiberoP3:
      diagnosis?.L1?.quotaPeloLiberoP3RischioIdraulico ?? undefined,
    // 138 — oggi su Level1
    quotaPeloLiberoP2:
      diagnosis?.L1?.quotaPeloLiberoP2RischioIdraulico ?? undefined,
    // 134 — oggi su Level1
    quotaMinimaIntradosso:
      diagnosis?.L1?.quotaMinimaIntradossoRischioIdraulico ?? undefined,
    // 141 — verificato: oggi su Common (NON Level1, come ipotizzato dal
    // piano). Prefill one-shot, poi editabile manualmente in L3 (§0.5), non
    // ricalcolato.
    francoIdraulicoFP2: diagnosis?.common?.francoIdraulicoFP2 ?? undefined,
    // 164 — verificato: oggi su Common (NON Level1, come ipotizzato dal
    // piano).
    evidenzeProfonditaDelleFondazioni:
      diagnosis?.common?.evidenzeProfonditaDelleFondazioni ?? undefined,
    // 158 — oggi su Level1 (nome proprietà "profonditaScavoMax")
    massimaProfonditaScavoLocalizzata:
      diagnosis?.L1?.profonditaScavoMax ?? undefined,
    // 159 — oggi su Level1
    profonditaFondazioneRispettoAlveo:
      diagnosis?.L1?.profonditaFondazioneRispettoAlveo ?? undefined,
    // 159b — oggi su Level1
    profonditaFondazioneRispettoAlveoFlag:
      diagnosis?.L1?.profonditaFondazioneRispettoAlveoFlag ?? undefined,
    ...deriveProfonditaFondazioneRispettoAlveoCheckboxes(
      diagnosis?.L1?.profonditaFondazioneRispettoAlveoFlag
    ),
    // 171 — verificato: oggi su Common (NON Level1, come ipotizzato dal
    // piano).
    protezioneAlPiedePileSpalle:
      diagnosis?.common?.protezioneAlPiedePileSpalle ?? undefined,
    // 172 — oggi su Level1
    statoConservazioneProtezionePiede:
      diagnosis?.L1?.statoConservazioneProtezionePiede ?? undefined,
  };
}
